/**
 * Banner di stato della freschezza dei dati di vulnerabilità (E10.10 — RF-9-2).
 *
 * Interroga il monitoring-service (E8) tramite GET /status/banner, che espone
 * lo stato aggregato più il dettaglio per sorgente (nvd, kev, ...): nome, ultimo
 * sync riuscito e età in ore. Il banner mostra l'elenco delle sorgenti stale così
 * che chi lo legge sappia subito su quale feed intervenire, invece di dover
 * indagare manualmente sul monitoring-service.
 *
 * Se l'endpoint non risponde o risponde con un errore (404, network error, ecc.),
 * il banner NON si mostra: degrada silenziosamente senza mostrare errori all'utente.
 */

import { useEffect, useState } from 'react';
import { SERVICE_URLS } from '../api/client.js';

// ---------------------------------------------------------------------------
// Risposta attesa da GET /status/banner (monitoring-service, E8.2.1/E8.2.3)
// ---------------------------------------------------------------------------

/** Dettaglio di freschezza per una singola sorgente (es. 'nvd', 'kev'). */
export interface FreshnessSourceDetail {
  name: string;
  lastSyncAt: string | null;
  ageHours: number | null;
}

interface StatusBannerResponse {
  state: 'ok' | 'stale';
  threshold_hours: number;
  computedAt: string;
  sources: FreshnessSourceDetail[];
}

// ---------------------------------------------------------------------------
// Hook useDbFreshness
// ---------------------------------------------------------------------------

/** Stato restituito dall'hook di freschezza. */
export interface DbFreshnessState {
  /** Se true, almeno una sorgente è stale (età > soglia, o mai sincronizzata). */
  isStale: boolean;
  /** Timestamp ISO del sync riuscito più vecchio tra le sorgenti stale (se disponibile). */
  lastUpdatedAt: string | null;
  /** Sorgenti stale, per permettere di indicare quale feed verificare. */
  staleSources: FreshnessSourceDetail[];
  /** Tutte le sorgenti riportate dal monitoring-service (per lo stato sempre visibile in header). */
  sources: FreshnessSourceDetail[];
  /** Soglia di staleness in ore, riportata dal monitoring-service. */
  thresholdHours: number | null;
}

const STATO_INIZIALE: DbFreshnessState = {
  isStale: false,
  lastUpdatedAt: null,
  staleSources: [],
  sources: [],
  thresholdHours: null,
};

/**
 * Interroga il monitoring-service (E8) per verificare la freschezza dei dati.
 *
 * Degrada silenziosamente se l'endpoint non è disponibile o restituisce un errore:
 * in tal caso isStale rimane false e il banner non si mostra.
 *
 * @param fetchFn - Funzione fetch iniettabile per i test. Default: globalThis.fetch.
 */
export function useDbFreshness(fetchFn: typeof fetch = globalThis.fetch): DbFreshnessState {
  const [state, setState] = useState<DbFreshnessState>(STATO_INIZIALE);

  useEffect(() => {
    let cancellato = false;

    async function caricaFreschezza() {
      try {
        const url = `${SERVICE_URLS.monitoring}/status/banner`;
        const risposta = await fetchFn(url);

        // Degrada silenziosamente se l'endpoint non è raggiungibile o risponde con errore
        if (!risposta.ok) return;

        const body = (await risposta.json()) as StatusBannerResponse;
        if (!Array.isArray(body.sources) || typeof body.state !== 'string') return;

        const staleSources = body.sources.filter(
          (s) => s.ageHours === null || s.ageHours > body.threshold_hours,
        );
        const isStale = body.state === 'stale' || staleSources.length > 0;

        const conSync = staleSources.filter(
          (s): s is FreshnessSourceDetail & { lastSyncAt: string } => s.lastSyncAt !== null,
        );
        const lastUpdatedAt =
          conSync.length > 0
            ? conSync.reduce(
                (oldest, s) => (s.lastSyncAt < oldest ? s.lastSyncAt : oldest),
                conSync[0]!.lastSyncAt,
              )
            : null;

        if (!cancellato) {
          setState({
            isStale,
            lastUpdatedAt,
            staleSources,
            sources: body.sources,
            thresholdHours: body.threshold_hours,
          });
        }
      } catch {
        // Endpoint non raggiungibile (network error, CORS, ecc.) → degrada silenziosamente
        // Il banner non si mostra: l'assenza del monitoring-service non deve disturbare l'utente
      }
    }

    void caricaFreschezza();

    return () => {
      cancellato = true;
    };
  }, [fetchFn]);

  return state;
}

// ---------------------------------------------------------------------------
// Stili inline (semplici — evita un CSS Module aggiuntivo per un banner globale)
// ---------------------------------------------------------------------------

const bannerStyle: React.CSSProperties = {
  backgroundColor: '#fff3cd',
  borderBottom: '2px solid #ffc107',
  color: '#664d03',
  padding: '0.5rem 1.5rem',
  fontSize: '0.88rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
};

const sourceListStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: '1.25rem',
};

// ---------------------------------------------------------------------------
// Componente StatusBanner
// ---------------------------------------------------------------------------

/** Props per testabilità: permette di iniettare fetchFn e stato direttamente. */
export interface StatusBannerProps {
  /**
   * Funzione fetch da usare per interrogare il monitoring-service.
   * Default: globalThis.fetch. Iniettabile nei test per mockare la risposta.
   */
  fetchFn?: typeof fetch;
  /**
   * Stato di freschezza iniettato direttamente (usato nei test per bypassare fetch).
   * Se fornito, skippa completamente l'hook useDbFreshness.
   */
  freshnessOverride?: DbFreshnessState;
}

/** Formatta l'età in ore di una sorgente in una stringa leggibile. */
function formattaEta(ageHours: number | null): string {
  if (ageHours === null) return 'mai sincronizzata';
  return `${ageHours.toFixed(1)}h fa`;
}

/**
 * fetchFn inerte: evita che useDbFreshness esegua una fetch reale quando il chiamante
 * ha già iniettato lo stato via freshnessOverride (es. Layout.tsx, che chiama l'hook
 * una sola volta e lo passa a StatusBanner + FreshnessStatus — senza questo, ciascuno
 * dei due rifarebbe la stessa GET /status/banner).
 */
const NOOP_FETCH: typeof fetch = () => Promise.reject(new Error('freshnessOverride attivo'));

/**
 * Banner globale che avvisa se i dati di vulnerabilità sono obsoleti (> soglia).
 * Renderizzato nel Layout.tsx sopra il contenuto principale.
 *
 * Il banner è invisibile se:
 *   - Tutte le sorgenti sono aggiornate
 *   - Il monitoring-service (E8) non è raggiungibile
 *   - Si verifica qualsiasi errore di rete/parsing
 *
 * Quando visibile, elenca le sorgenti stale (nome + età) per permettere di
 * agire direttamente sul feed interessato invece di dover indagare a mano.
 */
export function StatusBanner({ fetchFn, freshnessOverride }: StatusBannerProps = {}) {
  // Usa lo stato iniettato (test/Layout) oppure l'hook reale — se un override è fornito
  // evita di rifare la fetch (vedi commento su NOOP_FETCH).
  const hookState = useDbFreshness(freshnessOverride ? NOOP_FETCH : (fetchFn ?? globalThis.fetch));
  const { isStale, lastUpdatedAt, staleSources } = freshnessOverride ?? hookState;

  // Non mostrare nulla se i dati sono freschi o il monitoring-service non è disponibile
  if (!isStale) return null;

  const dataFormattata = lastUpdatedAt
    ? new Date(lastUpdatedAt).toLocaleString('it-IT')
    : 'sconosciuta';

  return (
    <div style={bannerStyle} role="alert" data-testid="status-banner-stale">
      <span>
        <span aria-hidden="true">⚠</span> I dati di vulnerabilità potrebbero non essere aggiornati
        (ultimo aggiornamento: <strong>{dataFormattata}</strong>). Verificare il monitoring-service
        (E8).
      </span>
      {staleSources.length > 0 && (
        <ul style={sourceListStyle} data-testid="status-banner-stale-sources">
          {staleSources.map((s) => (
            <li key={s.name} data-testid={`status-banner-stale-source-${s.name}`}>
              <strong>{s.name}</strong>: {formattaEta(s.ageHours)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente FreshnessStatus — indicatore sempre visibile (header)
// ---------------------------------------------------------------------------

const freshnessStatusStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.85rem',
  fontSize: '0.78rem',
  fontFamily: 'var(--font-mono)',
  color: 'var(--color-chrome-text-muted)',
};

const dotStyle: React.CSSProperties = {
  display: 'inline-block',
  width: '0.5rem',
  height: '0.5rem',
  borderRadius: '50%',
  marginRight: '0.35rem',
};

/** Props per testabilità: permette di iniettare fetchFn e stato direttamente. */
export interface FreshnessStatusProps {
  /** Funzione fetch iniettabile per i test. Default: globalThis.fetch. */
  fetchFn?: typeof fetch;
  /** Stato di freschezza iniettato direttamente (usato nei test per bypassare fetch). */
  freshnessOverride?: DbFreshnessState;
}

/**
 * Indicatore sempre visibile (non solo su stale) dello stato di freschezza per
 * ciascuna sorgente dati vulnerabilità (nvd, kev, ...). Renderizzato nell'header
 * del Layout, accanto allo spazio disponibile tra brand e utente.
 *
 * A differenza di StatusBanner (che si mostra solo se qualcosa è stale), questo
 * componente è sempre presente quando il monitoring-service risponde, per dare
 * visibilità continua sullo stato dei feed — non solo quando c'è già un problema.
 *
 * Nessun rendering se il monitoring-service non è raggiungibile o non ha ancora
 * sorgenti riportate (stesso degrado silenzioso di StatusBanner).
 */
export function FreshnessStatus({ fetchFn, freshnessOverride }: FreshnessStatusProps = {}) {
  const hookState = useDbFreshness(freshnessOverride ? NOOP_FETCH : (fetchFn ?? globalThis.fetch));
  const { sources, thresholdHours } = freshnessOverride ?? hookState;

  if (sources.length === 0) return null;

  return (
    <span style={freshnessStatusStyle} data-testid="freshness-status">
      {sources.map((s) => {
        const stale =
          s.ageHours === null || (thresholdHours !== null && s.ageHours > thresholdHours);
        return (
          <span
            key={s.name}
            title={`${s.name}: ultimo sync ${formattaEta(s.ageHours)}`}
            data-testid={`freshness-status-${s.name}`}
          >
            <span
              aria-hidden="true"
              style={{ ...dotStyle, backgroundColor: stale ? '#ffc107' : '#2ecc71' }}
            />
            {s.name} {formattaEta(s.ageHours)}
          </span>
        );
      })}
    </span>
  );
}
